/**
 * Login Audit Log viewer page — Admin only.
 *
 * Searchable/filterable login audit log viewer.
 * Filters: user, date range, event type, IP address.
 * Append-only, 7-year retention.
 *
 * Requirements: 53.6, 53.7, 53.8, 53.11, 53.12
 */

import { useState, useEffect, useCallback } from 'react';
import { adminService, LoginAuditLogEntry, LoginAuditFilter } from '../../services/adminService';

const EVENT_TYPES = ['Login', 'FailedLogin', 'Timeout', 'Logout'] as const;

const EVENT_TYPE_COLORS: Record<string, string> = {
  Login: 'bg-green-100 text-green-700',
  FailedLogin: 'bg-red-100 text-red-700',
  Timeout: 'bg-yellow-100 text-yellow-700',
  Logout: 'bg-gray-100 text-gray-700',
};

export function LoginAuditLog() {
  const [entries, setEntries] = useState<LoginAuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filter, setFilter] = useState<LoginAuditFilter>({
    limit: 50,
    offset: 0,
  });
  const [searchInput, setSearchInput] = useState('');

  const loadAudit = useCallback(async () => {
    try {
      setLoading(true);
      const result = await adminService.getLoginAudit(filter);
      setEntries(result.entries);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load login audit log.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const applySearch = () => {
    setFilter((prev) => ({ ...prev, search: searchInput || undefined, offset: 0 }));
  };

  const nextPage = () => {
    setFilter((prev) => ({ ...prev, offset: (prev.offset ?? 0) + (prev.limit ?? 50) }));
  };

  const prevPage = () => {
    setFilter((prev) => ({
      ...prev,
      offset: Math.max(0, (prev.offset ?? 0) - (prev.limit ?? 50)),
    }));
  };

  const formatTime = (iso: string) => new Date(iso).toLocaleString();

  const formatDuration = (seconds: number | null) => {
    if (seconds === null) return '—';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div >
      <h1 className="text-lg font-semibold text-text-primary mb-2">Login Audit Log</h1>
      <p className="text-sm text-slate-500 mb-6">
        Authentication event log. Append-only with 7-year retention.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm mb-5" role="alert">
          {error}
          <button className="ml-4 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search by user or IP..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-64"
          aria-label="Search login audit"
        />
        <input
          type="date"
          value={filter.dateFrom ?? ''}
          onChange={(e) => setFilter((prev) => ({ ...prev, dateFrom: e.target.value || undefined, offset: 0 }))}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          aria-label="Date from"
        />
        <input
          type="date"
          value={filter.dateTo ?? ''}
          onChange={(e) => setFilter((prev) => ({ ...prev, dateTo: e.target.value || undefined, offset: 0 }))}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          aria-label="Date to"
        />
        <select
          value={filter.eventType ?? ''}
          onChange={(e) => setFilter((prev) => ({ ...prev, eventType: e.target.value || undefined, offset: 0 }))}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          aria-label="Event type filter"
        >
          <option value="">All Events</option>
          {EVENT_TYPES.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="IP address"
          value={filter.ipAddress ?? ''}
          onChange={(e) => setFilter((prev) => ({ ...prev, ipAddress: e.target.value || undefined, offset: 0 }))}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-40"
          aria-label="IP address filter"
        />
        <button
          className="px-4 py-2 bg-baker-red text-white rounded-lg text-sm font-medium hover:bg-baker-red/90"
          onClick={applySearch}
        >
          Search
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="text-sm text-slate-500 mb-3">{total} event(s) found</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left bg-page">
                  <th className="py-2 px-3">Time</th>
                  <th className="py-2 px-3">Event</th>
                  <th className="py-2 px-3">User</th>
                  <th className="py-2 px-3">IP Address</th>
                  <th className="py-2 px-3">Auth Method</th>
                  <th className="py-2 px-3">Duration</th>
                  <th className="py-2 px-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.loginAuditId} className="border-b border-border hover:bg-page/50">
                    <td className="py-2 px-3">{formatTime(entry.timestamp)}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${EVENT_TYPE_COLORS[entry.eventType] ?? 'bg-gray-100'}`}>
                        {entry.eventType}
                      </span>
                    </td>
                    <td className="py-2 px-3">{entry.userId ?? '—'}</td>
                    <td className="py-2 px-3 font-mono">{entry.ipAddress ?? '—'}</td>
                    <td className="py-2 px-3">{entry.authMethod ?? '—'}</td>
                    <td className="py-2 px-3">{formatDuration(entry.sessionDuration)}</td>
                    <td className="py-2 px-3 text-gray-500 truncate max-w-xs">
                      {entry.failureReason ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {entries.length === 0 && (
            <p className="text-slate-400 text-center py-8">No login events found matching your filters.</p>
          )}

          {/* Pagination */}
          {total > (filter.limit ?? 50) && (
            <div className="flex items-center justify-between mt-4">
              <button
                className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page disabled:opacity-50"
                onClick={prevPage}
                disabled={(filter.offset ?? 0) === 0}
              >
                Previous
              </button>
              <span className="text-sm text-slate-500">
                Showing {(filter.offset ?? 0) + 1}–{Math.min((filter.offset ?? 0) + (filter.limit ?? 50), total)} of {total}
              </span>
              <button
                className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page disabled:opacity-50"
                onClick={nextPage}
                disabled={(filter.offset ?? 0) + (filter.limit ?? 50) >= total}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
